import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DataApi from '../../api/DataApi';
import './css/PermissionEdit.css';

const PermissionEdit = () => {
  const { permissionId, id: roleId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permission, setPermission] = useState(null);
  const [formData, setFormData] = useState({
    role_id: '',
    object_name: '',
    can_create: false,
    can_read: false,
    can_update: false,
    can_delete: false,
    is_active: true,
    is_standard: false
  });

  const permissionApi = new DataApi('permissions');

  useEffect(() => {
    if (permissionId) {
      fetchPermission();
    }
  }, [permissionId]);

  const fetchPermission = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await permissionApi.fetchById(permissionId);
      if (response.data.success) {
        const perm = response.data.data;
        setPermission(perm);
        setFormData({
          role_id: perm.role_id,
          object_name: perm.object_name,
          can_create: perm.can_create,
          can_read: perm.can_read,
          can_update: perm.can_update,
          can_delete: perm.can_delete,
          is_active: perm.is_active,
          is_standard: perm.is_standard
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch permission');
      console.error('Error fetching permission:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await permissionApi.update(formData, permissionId);
      if (response.data.success) {
        alert('Permission updated successfully!');
        navigate(`/setup/permission/${roleId}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update permission');
      console.error('Error updating permission:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/setup/permission/${roleId}`);
  };

  if (loading && !permission) {
    return (
      <div className="permission-edit-page">
        <div className="permission-edit-loading">Loading permission...</div>
      </div>
    );
  }

  return (
    <div className="permission-edit-page">
      <div className="permission-edit-header">
        <div className="permission-edit-header-content">
          <button className="permission-edit-btn permission-edit-btn-secondary permission-edit-back-btn" onClick={handleCancel}>
            ← Back to Permissions
          </button>
          <h2>Edit Permission</h2>
        </div>
      </div>

      {error && <div className="permission-edit-error-message">{error}</div>}

      <div className="permission-edit-content">
        <form onSubmit={handleSubmit}>
          <div className="permission-edit-form-section">
            <h3>Permission Details</h3>
            
            <div className="permission-edit-form-group">
              <label>Role ID</label>
              <input
                type="text"
                name="role_id"
                value={formData.role_id}
                onChange={handleInputChange}
                required
                disabled
              />
            </div>

            <div className="permission-edit-form-group">
              <label>Object Name</label>
              <input
                type="text"
                name="object_name"
                value={formData.object_name}
                onChange={handleInputChange}
                required
                disabled
              />
            </div>
          </div>

          <div className="permission-edit-form-section">
            <h3>Permissions</h3>
            
            <div className="permission-edit-permissions-grid">
              <div className="permission-edit-permission-checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="can_create"
                    checked={formData.can_create}
                    onChange={handleInputChange}
                  />
                  Can Create
                </label>
                <small>Allow creating new records</small>
              </div>
              
              <div className="permission-edit-permission-checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="can_read"
                    checked={formData.can_read}
                    onChange={handleInputChange}
                  />
                  Can Read
                </label>
                <small>Allow viewing records</small>
              </div>
              
              <div className="permission-edit-permission-checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="can_update"
                    checked={formData.can_update}
                    onChange={handleInputChange}
                  />
                  Can Update
                </label>
                <small>Allow editing records</small>
              </div>
              
              <div className="permission-edit-permission-checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="can_delete"
                    checked={formData.can_delete}
                    onChange={handleInputChange}
                  />
                  Can Delete
                </label>
                <small>Allow deleting records</small>
              </div>
            </div>
          </div>

          <div className="permission-edit-form-section">
            <h3>Settings</h3>
            
            <div className="permission-edit-form-group permission-edit-checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                Is Active
              </label>
              <small>Enable or disable this permission</small>
            </div>

            <div className="permission-edit-form-group permission-edit-checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="is_standard"
                  checked={formData.is_standard}
                  onChange={handleInputChange}
                />
                Is Standard
              </label>
              <small>Mark as a standard system permission</small>
            </div>
          </div>

          <div className="permission-edit-form-actions">
            <button 
              type="submit" 
              className="permission-edit-btn permission-edit-btn-primary"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Permission'}
            </button>
            <button 
              type="button" 
              className="permission-edit-btn permission-edit-btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PermissionEdit;
